import { ref, reactive, onMounted, onUnmounted, computed, inject, nextTick } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface HeroState {
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  backgroundVideo: Ref<HTMLVideoElement | null>
  showScrollIndicator: Ref<boolean>
  heroData: {
    title: string
    titleImage: string
    titleImageAlt: string
    subtitle: string
    description: string
    backgroundImage: string
    backgroundVideo: string
    steamUrl: string
    wishlistButtonText: string
    secondaryButtonUrl: string
    secondaryButtonText: string
    features: string[]
  }
  backgroundStyles: any
  prefersReducedMotion: any
  fetchHeroData: () => Promise<void>
  trackSteamClick: (event: Event) => void
  trackSupportClick: (event: Event) => void
  scrollToContent: () => void
  $t: (key: string, fallback?: string) => string
}

export default function useHero(passedContent?: any): HeroState {
  // Services & i18n
  const ContentService: any = inject('ContentService')
  const { t, locale } = useI18n()

  // Reactive state
  const isLoading = ref(true)
  const hasError = ref(false)
  const backgroundVideo = ref<HTMLVideoElement | null>(null)
  const showScrollIndicator = ref(true)

  // Hero data
  const heroData = reactive({
    title: '',
    titleImage: '',
    titleImageAlt: '',
    subtitle: '',
    description: '',
    backgroundImage: '',
    backgroundVideo: '',
    steamUrl: '',
    wishlistButtonText: '',
    secondaryButtonUrl: '',
    secondaryButtonText: '',
    features: [] as string[]
  })

  // Computed properties
  const backgroundStyles = computed(() => {
    const styles: Record<string, string> = {}
    
    if (heroData.backgroundImage) {
      styles.backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${heroData.backgroundImage})`
      styles.backgroundSize = 'cover'
      styles.backgroundPosition = 'center'
      styles.backgroundRepeat = 'no-repeat'
    }
    
    return styles
  })

  const prefersReducedMotion = computed(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  // Helpers
  const normalizeFeatures = (raw: unknown): string[] => {
    if (!raw) return []
    if (Array.isArray(raw)) return raw.map((x) => String(x))
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.map((x) => String(x)) : []
      } catch {
        return []
      }
    }
    return []
  }

  const applyContent = (content: any) => {
    heroData.title = content?.title || content?.heroTitle || content?.name || ''
    
    const titleImg = content?.titleImage || content?.heroTitleImage
    heroData.titleImage = ContentService?.getMediaUrl ? (ContentService.getMediaUrl(titleImg) || '') : (titleImg || '')
    heroData.titleImageAlt = content?.titleImageAlt || content?.heroTitleImageAlt || 'Hero Title'
    
    heroData.subtitle = content?.subtitle || content?.heroSubtitle || ''
    heroData.description = content?.description || content?.heroDescription || ''

    const bgImg = content?.backgroundImage || content?.heroBackgroundImage
    heroData.backgroundImage = ContentService?.getMediaUrl ? (ContentService.getMediaUrl(bgImg) || '') : (bgImg || '')

    const bgVid = content?.backgroundVideo || content?.heroBackgroundVideo
    heroData.backgroundVideo = ContentService?.getMediaUrl ? (ContentService.getMediaUrl(bgVid) || '') : (bgVid || '')

    heroData.steamUrl = content?.steamUrl || content?.steamWishlistUrl || ''
    heroData.wishlistButtonText = content?.wishlistButtonText || ''

    heroData.secondaryButtonUrl = content?.heroSecondaryButtonUrl || content?.secondaryButtonUrl || content?.ctaUrl || ''
    heroData.secondaryButtonText = content?.heroSecondaryButtonText || content?.secondaryButtonText || content?.ctaText || ''

    heroData.features = normalizeFeatures(content?.heroFeatures || content?.features)
  }

  // Methods
  const fetchHeroData = async () => {
    try {
      // If parent passed content, show it immediately without spinner, then fetch fresh data
      isLoading.value = !passedContent
      hasError.value = false

      if (passedContent) {
        applyContent(passedContent)
      }

      const culture = locale.value === 'nl' ? 'nl-nl' : 'en-us'
      const homepageData = await ContentService?.getHomepage?.(culture)
      if (homepageData) {
        applyContent(homepageData)
      }
    } catch (error) {
      console.error('Error fetching hero data:', error)
      hasError.value = true
      // No hardcoded content here; leave fields empty so CMS controls content
      applyContent({})
    } finally {
      isLoading.value = false
    }
  }

  const trackSteamClick = (event: Event) => {
    try {
      if (typeof window !== 'undefined') {
        const analytics = (window as any).ScriptonicAnalytics
        if (analytics && typeof analytics.trackEvent === 'function') {
          // Track the event (tracker uses sendBeacon, so no need to delay)
          analytics.trackEvent('steam_wishlist_click', 'engagement', {
            label: 'hero_section',
            source: 'hero_banner'
          })
          console.log('[Analytics] Steam wishlist button clicked from hero section')
        } else {
          console.warn('[Analytics] Tracker not loaded')
        }
      }
    } catch (error) {
      console.error('[Analytics] Error tracking Steam click:', error)
    }
  }

  const trackSupportClick = (event: Event) => {
    try {
      if (typeof window !== 'undefined') {
        const analytics = (window as any).ScriptonicAnalytics
        if (analytics && typeof analytics.trackEvent === 'function') {
          // Track the event
          analytics.trackEvent('support_us_click', 'engagement', {
            label: 'hero_section',
            buttonText: heroData.secondaryButtonText,
            source: 'hero_banner'
          })
          console.log('[Analytics] Support Us button clicked from hero section')
        } else {
          console.warn('[Analytics] Tracker not loaded yet')
        }
      }
    } catch (error) {
      console.error('[Analytics] Error tracking support click:', error)
    }
  }

  const scrollToContent = () => {
    if (typeof document === 'undefined') return
    const nextSection = document.querySelector('.carousel-section, .about-section, main > section:nth-child(2)') as HTMLElement | null
    if (nextSection) {
      nextSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const handleScroll = () => {
    if (typeof window === 'undefined') return
    const scrollY = window.scrollY
    const viewportHeight = window.innerHeight
    showScrollIndicator.value = scrollY < viewportHeight * 0.3
  }

  // Lifecycle
  onMounted(async () => {
    await fetchHeroData()

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true })
    }

    await nextTick()
    if (backgroundVideo.value) {
      try {
        await backgroundVideo.value.play()
      } catch (error) {
        // Autoplay prevented by browser
      }
    }
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  return {
    isLoading,
    hasError,
    backgroundVideo,
    showScrollIndicator,
    heroData,
    backgroundStyles,
    prefersReducedMotion,
    fetchHeroData,
    trackSteamClick,
    trackSupportClick,
    scrollToContent,
    $t: t
  }
}
